// src/features/analytics/eventWindow.ts
//
// Pure helpers for event windows (dashboard ADR-009 / api ADR-0015): the
// client-side mirror of the gateway's outward hour snap (so the page can show
// the effective window before the response lands), broadcast padding, the
// hourly-vs-daily trend grain, hour gap-filling, and local-time labels. No
// wall clock, no locale-dependent formatting beyond Luxon's fixed formats.

import { DateTime } from 'luxon';
import {
  BROADCAST_PAD_SECONDS,
  HOURLY_MAX_RANGE_DAYS,
  HOURLY_TREND_MAX_DAYS,
} from '@/lib/constants/analytics';
import { SECONDS_PER_DAY, snapRangeToDays, type DayZone, type UtcRange } from '@/shared/utils/utcRange';
import type { BroadcastRow, ConcurrencyPoint, PlaybackHourlyPoint } from './types';

export const SECONDS_PER_HOUR = 3600;

/** An event window: end-exclusive epoch-second instants. */
export interface EventWindow {
  from: number;
  to: number;
}

export type TrendGrain = 'hourly' | 'daily';

/** True for a non-empty window at or under the gateway's 92-day hour-grain cap. */
export function isValidEventWindow(from: number, to: number): boolean {
  return Number.isInteger(from) && Number.isInteger(to) && from < to && to - from <= HOURLY_MAX_RANGE_DAYS * SECONDS_PER_DAY;
}

/**
 * The gateway's outward hour snap: floor(from), ceil(to). A 6:30–9:30 window
 * reports 6:00–10:00 — the page shows this as the "effective window" so the
 * hour alignment is never a surprise.
 */
export function snapWindowToHours(window: EventWindow): EventWindow {
  const from = Math.floor(window.from / SECONDS_PER_HOUR) * SECONDS_PER_HOUR;
  const to = Math.ceil(window.to / SECONDS_PER_HOUR) * SECONDS_PER_HOUR;
  return { from, to: to > from ? to : from + SECONDS_PER_HOUR };
}

/** Pad a window on both sides (the "hour before to the hour after" ask). */
export function padWindow(window: EventWindow, padSeconds: number = BROADCAST_PAD_SECONDS): EventWindow {
  return { from: window.from - padSeconds, to: window.to + padSeconds };
}

/** The window a broadcast pick writes: the run itself, optionally padded by an hour each side. */
export function broadcastWindow(row: BroadcastRow, pad: boolean): EventWindow {
  const base = { from: row.start, to: row.end };
  return pad ? padWindow(base) : base;
}

/** Hours for short windows (a game, an evening), days beyond HOURLY_TREND_MAX_DAYS. */
export function trendGrainFor(window: EventWindow): TrendGrain {
  return window.to - window.from <= HOURLY_TREND_MAX_DAYS * SECONDS_PER_DAY ? 'hourly' : 'daily';
}

/**
 * Zero-fill missing hours across an hour-aligned end-exclusive window so the
 * axis is continuous. Points outside the window pass through at their sorted
 * position (defensive; the API only returns in-window hours).
 */
export function fillHourlyGaps(points: PlaybackHourlyPoint[], window: EventWindow): PlaybackHourlyPoint[] {
  const byHour = new Map(points.map((p) => [p.hour, p]));
  const out: PlaybackHourlyPoint[] = [];
  for (let hour = window.from; hour < window.to; hour += SECONDS_PER_HOUR) {
    out.push(byHour.get(hour) ?? { hour, sessions: 0, watchTimeMs: 0, uniqueViewers: 0 });
  }
  return out;
}

/** Hour axis label — a point in time, rendered in the viewer's zone ("Sep 11, 7PM"). */
export function hourLabel(hourSeconds: number, zone: DayZone = 'local'): string {
  return DateTime.fromSeconds(hourSeconds, { zone: zone === 'utc' ? 'utc' : zone }).toFormat('MMM d, ha');
}

/** Instant label with minutes ("Sep 11, 7:02 PM"), in the viewer's zone. */
export function instantLabel(seconds: number, zone: DayZone = 'local'): string {
  return DateTime.fromSeconds(seconds, { zone: zone === 'utc' ? 'utc' : zone }).toFormat('MMM d, h:mm a');
}

/**
 * Window label: "Sep 11, 6:00 PM – 10:00 PM" within one day, "Sep 11, 6:00 PM
 * – Sep 12, 1:00 AM" across days. `to` is exclusive and rendered as-is (the
 * boundary instant), matching how the gateway echoes `effectiveTo`.
 */
export function windowLabel(window: EventWindow, zone: DayZone = 'local'): string {
  const z = zone === 'utc' ? 'utc' : zone;
  const from = DateTime.fromSeconds(window.from, { zone: z });
  const to = DateTime.fromSeconds(window.to, { zone: z });
  if (from.hasSame(to, 'day')) {
    return `${from.toFormat('MMM d, h:mm a')} – ${to.toFormat('h:mm a')}`;
  }
  return `${from.toFormat('MMM d, h:mm a')} – ${to.toFormat('MMM d, h:mm a')}`;
}

/** Whole minutes in a window ("148 min", "2h 28m" is left to the caller). */
export function windowMinutes(window: EventWindow): number {
  return Math.round((window.to - window.from) / 60);
}

/** Hour-tick thinning for the hourly chart (recharts XAxis `interval` semantics). */
export function hourlyTickInterval(hours: number): number {
  if (hours <= 12) return 0;
  if (hours <= 48) return 3;
  if (hours <= 96) return 11;
  return 23;
}

// ---------------------------------------------------------------------------
// Stream report window modes (dashboard ADR-009, amended 2026-09-13): the
// page opens on TODAY (local day → the current hour), and one control offers
// Broadcast / Day / Range. Everything below is pure so the state walk is a
// unit test, not a checklist.

/** The end of the hour containing `seconds` (an instant already on the hour maps to itself). */
export function ceilHourSeconds(seconds: number): number {
  return Math.ceil(seconds / SECONDS_PER_HOUR) * SECONDS_PER_HOUR;
}

/**
 * The page's default window: the calendar day containing `nowSeconds` in
 * `zone`, from its midnight to the end of the current hour. Always at least
 * one hour wide (at exactly midnight it is the first hour of the day). Zone
 * math goes through Luxon so a DST day is 23 or 25 hours, never a fixed stride.
 */
export function todayWindow(nowSeconds: number, zone: DayZone): EventWindow {
  const from = DateTime.fromSeconds(nowSeconds, { zone: zone === 'utc' ? 'utc' : zone }).startOf('day').toSeconds();
  const to = ceilHourSeconds(nowSeconds);
  return { from, to: to > from ? to : from + SECONDS_PER_HOUR };
}

/**
 * The window for one calendar day given as 'yyyy-MM-dd' in `zone`: a past day
 * is the whole day; today ends at the current hour (same as todayWindow); a
 * future or unparseable date yields null.
 */
export function dayWindow(date: string, nowSeconds: number, zone: DayZone): EventWindow | null {
  const z = zone === 'utc' ? 'utc' : zone;
  const start = DateTime.fromFormat(date, 'yyyy-MM-dd', { zone: z });
  if (!start.isValid) return null;
  const from = start.startOf('day').toSeconds();
  if (from > nowSeconds) return null;
  const nextDay = start.startOf('day').plus({ days: 1 }).toSeconds();
  if (nextDay <= nowSeconds) return { from, to: nextDay };
  return todayWindow(nowSeconds, zone);
}

/** 'yyyy-MM-dd' of the calendar day containing `seconds` in `zone`. */
export function dayStringOf(seconds: number, zone: DayZone): string {
  return DateTime.fromSeconds(seconds, { zone: zone === 'utc' ? 'utc' : zone }).toFormat('yyyy-MM-dd');
}

/** Shift a 'yyyy-MM-dd' day by whole calendar days in `zone` (DST-safe). */
export function shiftDay(date: string, days: number, zone: DayZone): string {
  const z = zone === 'utc' ? 'utc' : zone;
  return DateTime.fromFormat(date, 'yyyy-MM-dd', { zone: z }).plus({ days }).toFormat('yyyy-MM-dd');
}

/**
 * Range mode: a start date and an end date (end INCLUSIVE as a calendar day),
 * whole days unless hours are set — start hour 0–23 on the start date, end
 * hour 1–24 on the end date (exclusive; 24 = the midnight closing the end
 * date, the default). Whole hours by construction, so the requested window IS
 * the effective window. Null when the dates do not parse, the end is not
 * after the start, or the span exceeds the hour-grain cap.
 */
export function rangeWindow(
  startDate: string,
  endDate: string,
  zone: DayZone,
  startHour: number = 0,
  endHour: number = 24
): EventWindow | null {
  const z = zone === 'utc' ? 'utc' : zone;
  const s = DateTime.fromFormat(startDate, 'yyyy-MM-dd', { zone: z });
  const e = DateTime.fromFormat(endDate, 'yyyy-MM-dd', { zone: z });
  if (!s.isValid || !e.isValid) return null;
  if (!Number.isInteger(startHour) || !Number.isInteger(endHour)) return null;
  if (startHour < 0 || startHour > 23 || endHour < 1 || endHour > 24) return null;
  const from = s.startOf('day').plus({ hours: startHour }).toSeconds();
  const to = e.startOf('day').plus({ hours: endHour }).toSeconds();
  return isValidEventWindow(from, to) ? { from, to } : null;
}

export interface RangeFields {
  startDate: string;
  startHour: number;
  endDate: string;
  endHour: number;
}

/** Range-mode field values for a window: start date/hour and end date/hour (end exclusive, 24 = midnight closing the end date). */
export function rangeFieldsFor(window: EventWindow, zone: DayZone): RangeFields {
  const z = zone === 'utc' ? 'utc' : zone;
  const from = DateTime.fromSeconds(window.from, { zone: z });
  const to = DateTime.fromSeconds(window.to, { zone: z });
  const endIsMidnight = to.hour === 0 && to.minute === 0;
  return {
    startDate: from.toFormat('yyyy-MM-dd'),
    startHour: from.hour,
    endDate: (endIsMidnight ? to.minus({ days: 1 }) : to).toFormat('yyyy-MM-dd'),
    endHour: endIsMidnight ? 24 : to.hour + (to.minute > 0 || to.second > 0 ? 1 : 0),
  };
}

/** True when the fields describe whole days (no hour refinement). */
export function rangeIsWholeDays(fields: RangeFields): boolean {
  return fields.startHour === 0 && fields.endHour === 24;
}

/** Select options for an hour of the day: value '0'…'23' (start) or '1'…'24' (end, 24 = "12 AM (end of day)"). */
export function hourOptions(edge: 'start' | 'end'): { value: string; label: string }[] {
  const hours = edge === 'start' ? Array.from({ length: 24 }, (_, i) => i) : Array.from({ length: 24 }, (_, i) => i + 1);
  return hours.map((h) => ({ value: String(h), label: hourOfDayLabel(h) }));
}

/** "12 AM", "1 PM", … ; 24 reads "12 AM (end of day)". */
export function hourOfDayLabel(hour: number): string {
  if (hour === 24) return '12 AM (end of day)';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12} ${hour < 12 ? 'AM' : 'PM'}`;
}

/**
 * Raise a window's start to the product-window floor (the gateway refuses
 * ranges that begin before it). A window entirely before the floor becomes
 * the first hour at the floor. `clamped` tells the page to say so.
 */
export function floorWindow(window: EventWindow, floorSeconds: number | null): { window: EventWindow; clamped: boolean } {
  if (floorSeconds === null || window.from >= floorSeconds) return { window, clamped: false };
  const to = window.to > floorSeconds ? window.to : floorSeconds + SECONDS_PER_HOUR;
  return { window: { from: floorSeconds, to }, clamped: true };
}

/** The mode the control opens in for a given window: a broadcast, one calendar day, or a range. */
export function inferWindowMode(
  window: EventWindow,
  nowSeconds: number,
  zone: DayZone,
  isBroadcast: boolean
): 'broadcast' | 'day' | 'range' {
  if (isBroadcast) return 'broadcast';
  const day = dayWindow(dayStringOf(window.from, zone), nowSeconds, zone);
  if (day && day.from === window.from && day.to === window.to) return 'day';
  return 'range';
}

/**
 * The effective-window line under the control: "Sat, Sep 13 · 10 AM – 3 PM CDT
 * · 5 hours" within one day; "Fri, Sep 11, 7 PM – Sat, Sep 12, 1 AM CDT ·
 * 6 hours" across days. Minutes appear only when a bound is not on the hour.
 */
export function effectiveWindowLabel(window: EventWindow, zone: DayZone = 'local'): string {
  const z = zone === 'utc' ? 'utc' : zone;
  const from = DateTime.fromSeconds(window.from, { zone: z });
  const to = DateTime.fromSeconds(window.to, { zone: z });
  const time = (d: DateTime) => d.toFormat(d.minute === 0 ? 'h a' : 'h:mm a');
  const abbrev = zone === 'utc' ? 'UTC' : to.toFormat('ZZZZ');
  const minutes = Math.round((window.to - window.from) / 60);
  const duration =
    minutes % 60 === 0
      ? `${minutes / 60} ${minutes === 60 ? 'hour' : 'hours'}`
      : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
  if (from.hasSame(to, 'day') || (to.hour === 0 && to.minute === 0 && from.hasSame(to.minus({ minutes: 1 }), 'day'))) {
    return `${from.toFormat('ccc, MMM d')} · ${time(from)} – ${time(to)} ${abbrev} · ${duration}`;
  }
  return `${from.toFormat('ccc, MMM d')}, ${time(from)} – ${to.toFormat('ccc, MMM d')}, ${time(to)} ${abbrev} · ${duration}`;
}

/** Day-mode navigation: ‹ disabled at the floor, › disabled on today, "Back to today" only off today. */
export function dayNavState(day: string, today: string, floorDay?: string): {
  canGoBack: boolean;
  canGoForward: boolean;
  showBackToToday: boolean;
} {
  return {
    canGoBack: floorDay === undefined || day > floorDay,
    canGoForward: day < today,
    showBackToToday: day !== today,
  };
}

/** True when `window` is exactly the page's default (today so far) in `zone`. */
export function isTodayWindow(window: EventWindow, nowSeconds: number, zone: DayZone): boolean {
  const t = todayWindow(nowSeconds, zone);
  return t.from === window.from && t.to === window.to;
}

/** Title for the totals card's empty state. */
export function emptyTotalsTitle(isToday: boolean): string {
  return isToday ? 'No viewers today so far' : 'No viewers in this window';
}

export interface EmptyTotalsAction {
  label: string;
  window: EventWindow;
}

/**
 * The ONE action offered under an empty totals card: the latest broadcast
 * (padded, the picker's default) when any exist; otherwise yesterday when the
 * window is today; otherwise nothing (a hand-picked past window with no
 * viewers is simply the answer).
 */
export function emptyTotalsAction(
  window: EventWindow,
  nowSeconds: number,
  zone: DayZone,
  broadcasts: readonly BroadcastRow[]
): EmptyTotalsAction | null {
  if (broadcasts.length > 0) {
    const latest = broadcasts.reduce((a, b) => (b.start > a.start ? b : a));
    return { label: 'Show the latest broadcast', window: broadcastWindow(latest, true) };
  }
  if (isTodayWindow(window, nowSeconds, zone)) {
    const yesterday = dayWindow(shiftDay(dayStringOf(nowSeconds, zone), -1, zone), nowSeconds, zone);
    if (yesterday) return { label: 'See yesterday', window: yesterday };
  }
  return null;
}

// ---------------------------------------------------------------------------
// One window drives the whole stream page (ADR-009 amendment 2026-09-13):
// the UTC-day panels cover the UTC days that contain the window, and the two
// time charts share one epoch-second axis.

/** The UTC calendar days that contain the window (outward snap) — what the UTC-day panels query. */
export function coveringUtcDays(window: EventWindow): UtcRange {
  return snapRangeToDays(window, 'utc');
}

/** Hours between hour ticks for a window of `hours` hours: 1 / 2 / 3 / 6 / 12 / 24. */
export function hourTickStep(hours: number): number {
  if (hours <= 12) return 1;
  if (hours <= 24) return 2;
  if (hours <= 48) return 3;
  if (hours <= 96) return 6;
  if (hours <= 192) return 12;
  return 24;
}

/**
 * Tick positions (epoch seconds) for an hour-aligned window: every step from
 * the window start, always ending on the window's end boundary. Both time
 * charts pass the same list so their axes line up.
 */
export function hourTicks(window: EventWindow): number[] {
  const snapped = snapWindowToHours(window);
  const hours = Math.round((snapped.to - snapped.from) / SECONDS_PER_HOUR);
  const step = hourTickStep(hours) * SECONDS_PER_HOUR;
  const ticks: number[] = [];
  for (let t = snapped.from; t < snapped.to; t += step) ticks.push(t);
  ticks.push(snapped.to);
  return ticks;
}

/** True when the window's first and last instants fall on different calendar days in `zone`. */
export function windowSpansDays(window: EventWindow, zone: DayZone = 'local'): boolean {
  const z = zone === 'utc' ? 'utc' : zone;
  return !DateTime.fromSeconds(window.from, { zone: z }).hasSame(DateTime.fromSeconds(window.to - 1, { zone: z }), 'day');
}

/** Axis tick label: "9 AM" within one day, "Sep 13, 9 AM" when the window spans days; minutes only off the hour. */
export function axisTickLabel(seconds: number, multiDay: boolean, zone: DayZone = 'local'): string {
  const d = DateTime.fromSeconds(seconds, { zone: zone === 'utc' ? 'utc' : zone });
  const time = d.toFormat(d.minute === 0 ? 'h a' : 'h:mm a');
  return multiDay ? `${d.toFormat('MMM d')}, ${time}` : time;
}

/**
 * Zero-fill a concurrency series at its bucket interval across the window so
 * the line starts at the window start, not at the first viewer. Present points
 * are kept as-is; points outside the window pass through at their sorted
 * position. A window needing more than `maxPoints` buckets is returned as-is
 * (sorted) — never more points than the gateway's own cap.
 */
export function fillConcurrencyGaps(
  points: ConcurrencyPoint[],
  window: EventWindow,
  interval: 'minute' | 'hour',
  maxPoints: number
): ConcurrencyPoint[] {
  const step = interval === 'hour' ? SECONDS_PER_HOUR : 60;
  const from = Math.ceil(window.from / step) * step;
  const buckets = Math.ceil((window.to - from) / step);
  const sorted = [...points].sort((a, b) => a.bucket - b.bucket);
  if (buckets <= 0 || buckets > maxPoints) return sorted;
  const byBucket = new Map(sorted.map((p) => [p.bucket, p]));
  const out: ConcurrencyPoint[] = sorted.filter((p) => p.bucket < from);
  for (let b = from; b < window.to; b += step) {
    out.push(byBucket.get(b) ?? { bucket: b, peakConcurrent: 0, avgConcurrent: 0, builtAt: 0 });
  }
  for (const p of sorted) if (p.bucket >= window.to) out.push(p);
  return out;
}

/** A chart row for one hour bucket: the bucket keeps its `hour` (tooltip span) and gains `x`, the hour's midpoint. */
export type HourMidpointRow<T extends { hour: number }> = T & { x: number };

/**
 * Smooth-curve placement for the hourly chart: each bucket is plotted at the
 * MIDDLE of its hour (`hour + 1800 s`) so the value sits over the hour it
 * describes rather than at its start edge. Order preserved; empty → empty.
 */
export function hourMidpoints<T extends { hour: number }>(points: T[]): HourMidpointRow<T>[] {
  return points.map((p) => ({ ...p, x: p.hour + SECONDS_PER_HOUR / 2 }));
}

/** True when a bucket carries no viewing at all. */
export function isZeroHourBucket(p: PlaybackHourlyPoint): boolean {
  return p.sessions === 0 && p.watchTimeMs === 0 && p.uniqueViewers === 0;
}

/**
 * Honest domain edges for the smooth curve: a zero-value point is prepended at
 * `from` / appended at `to` ONLY when the first / last bucket is zero-valued
 * anyway, so the area reaches the boundary without fabricating a non-zero
 * edge. A non-zero first bucket starts the curve at its own midpoint. Edge
 * rows are flagged so the tooltip skips them. Empty → empty.
 */
export function withZeroEdges<T extends PlaybackHourlyPoint>(
  rows: HourMidpointRow<T>[],
  from: number,
  to: number
): (HourMidpointRow<T> & { edge?: boolean })[] {
  if (rows.length === 0) return [];
  const zero = (x: number, hour: number) =>
    ({ ...rows[0], hour, x, sessions: 0, watchTimeMs: 0, uniqueViewers: 0, edge: true }) as HourMidpointRow<T> & { edge: boolean };
  const out: (HourMidpointRow<T> & { edge?: boolean })[] = [];
  const first = rows[0];
  const last = rows[rows.length - 1];
  if (isZeroHourBucket(first) && first.x > from) out.push(zero(from, first.hour));
  out.push(...rows);
  if (isZeroHourBucket(last) && last.x < to) out.push(zero(to, last.hour));
  return out;
}

/** Hour-bucket span label: "8 AM – 9 AM", or "Sep 13, 8 AM – 9 AM" when the window spans days. */
export function hourSpanLabel(hourSeconds: number, multiDay: boolean, zone: DayZone = 'local'): string {
  const z = zone === 'utc' ? 'utc' : zone;
  const start = DateTime.fromSeconds(hourSeconds, { zone: z });
  const end = start.plus({ hours: 1 });
  const span = `${start.toFormat('h a')} – ${end.toFormat('h a')}`;
  return multiDay ? `${start.toFormat('MMM d')}, ${span}` : span;
}
